import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import { 
  BookOpen, ArrowLeft, ChevronRight, ChevronDown, Play, FileText, 
  Video, File, ExternalLink, Download, HelpCircle, Image, Music,
  CheckCircle, Circle, Lock, Clock, Award, Users, Layers, GraduationCap
} from 'lucide-react';
import { toast } from 'sonner';
import { resolveUploadUrl } from '../../utils/media';

const lessonTypeIcons = {
  text: FileText,
  video: Video,
  pdf: File,
  link: ExternalLink,
  file: Download,
  quiz: HelpCircle,
  image: Image,
  audio: Music
};

const StudentCoursePage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedModules, setExpandedModules] = useState({});

  const getAvatarUrl = (avatarUrl) => resolveUploadUrl(avatarUrl);
  
  const fetchCourse = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch course structure
      const courseResponse = await api.get(`/content/courses/${courseId}`);
      setCourse(courseResponse.data);
      
      // Fetch student progress
      try {
        const progressResponse = await api.get(`/content/student/course-progress/${courseId}`);
        setProgress(progressResponse.data);
      } catch (err) {
        // Progress might not exist yet
        setProgress({ completed_lesson_ids: [], progress_percent: 0 });
      }
      
      // Expand first module by default
      if (courseResponse.data.modules?.length > 0) {
        setExpandedModules({ [courseResponse.data.modules[0].id]: true });
      }
      
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Erreur lors du chargement du cours');
    } finally {
      setLoading(false);
    }
  }, [courseId]);
  
  useEffect(() => {
    fetchCourse();
  }, [fetchCourse]);
  
  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };
  
  const isLessonCompleted = (lessonId) => {
    return progress?.completed_lesson_ids?.includes(lessonId);
  };
  
  const getModuleProgress = (module) => {
    if (!module.lessons || module.lessons.length === 0) return 0;
    const completed = module.lessons.filter(l => isLessonCompleted(l.id)).length;
    return Math.round((completed / module.lessons.length) * 100);
  };
  
  const getNextLesson = () => {
    if (!course?.modules) return null;
    
    for (const module of course.modules) {
      for (const lesson of module.lessons || []) {
        if (!isLessonCompleted(lesson.id) && lesson.is_published) {
          return { lesson, module };
        }
      }
    }
    return null;
  };
  
  const nextLesson = getNextLesson();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <BookOpen className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">Cours non trouvé</p>
          <Button onClick={() => navigate('/student/courses')} className="mt-4">
            Retour aux cours
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-start gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/student/courses')}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-violet-100 text-violet-700">
                  {course.level === 'beginner' ? 'Débutant' : 
                   course.level === 'intermediate' ? 'Intermédiaire' : 'Avancé'}
                </Badge>
                {course.category && (
                  <Badge variant="outline">{course.category}</Badge>
                )}
              </div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                {course.name}
              </h1>
              {course.description && (
                <p className="text-slate-600 dark:text-slate-400 max-w-3xl">
                  {course.description}
                </p>
              )}
              
              {/* Course Stats */}
              <div className="flex flex-wrap items-center gap-6 mt-4 text-sm text-slate-500">
                <div className="flex items-center gap-1">
                  <Layers className="w-4 h-4" />
                  <span>{course.module_count || 0} modules</span>
                </div>
                <div className="flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  <span>{course.lesson_count || 0} leçons</span>
                </div>
                {(course.duration_calculated?.total_minutes > 0 || course.duration_hours) && (
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>
                      {course.duration_calculated?.total_minutes > 0
                        ? course.duration_calculated.formatted
                        : `${course.duration_hours}h`}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Trainer Info */}
              {course.trainer_name && (
                <div className="flex items-center gap-3 mt-4 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg w-fit">
                  <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center">
                    {course.trainer_avatar_url ? (
                      <img 
                        src={getAvatarUrl(course.trainer_avatar_url)} 
                        alt={course.trainer_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <GraduationCap className="w-6 h-6 text-white" />
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Formateur</p>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {course.trainer_name}
                    </p>
                    {course.trainer_specialization && (
                      <p className="text-sm text-emerald-600 dark:text-emerald-400">
                        {course.trainer_specialization}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Progress Card */}
            <Card className="w-64 hidden lg:block">
              <CardContent className="p-4">
                <div className="text-center mb-3">
                  <div className="text-3xl font-bold text-violet-600">
                    {progress?.progress_percent || 0}%
                  </div>
                  <p className="text-sm text-slate-500">Progression</p>
                </div>
                <Progress value={progress?.progress_percent || 0} className="h-2 mb-4" />
                
                {nextLesson ? (
                  <Button 
                    className="w-full bg-violet-600 hover:bg-violet-700"
                    onClick={() => navigate(`/student/courses/${courseId}/lessons/${nextLesson.lesson.id}`)}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Continuer
                  </Button>
                ) : progress?.progress_percent === 100 ? (
                  <div className="flex items-center justify-center gap-2 text-emerald-600">
                    <Award className="w-5 h-5" />
                    <span className="font-medium">Cours terminé !</span>
                  </div>
                ) : (
                  <Button 
                    className="w-full bg-violet-600 hover:bg-violet-700"
                    onClick={() => {
                      const firstLesson = course.modules?.[0]?.lessons?.[0];
                      if (firstLesson) {
                        navigate(`/student/courses/${courseId}/lessons/${firstLesson.id}`);
                      }
                    }}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Commencer
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Course Content */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-violet-500" />
                  Contenu du cours
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {course.modules?.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <Layers className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Aucun contenu disponible</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-200 dark:divide-slate-700">
                    {course.modules?.map((module, moduleIndex) => {
                      const moduleProgress = getModuleProgress(module);
                      const isExpanded = expandedModules[module.id];
                      
                      return (
                        <div key={module.id}>
                          {/* Module Header */}
                          <button
                            onClick={() => toggleModule(module.id)}
                            className="w-full p-4 flex items-center gap-4 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left"
                          >
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              moduleProgress === 100 
                                ? 'bg-emerald-100 text-emerald-600' 
                                : 'bg-violet-100 text-violet-600'
                            }`}>
                              {moduleProgress === 100 ? (
                                <CheckCircle className="w-5 h-5" />
                              ) : (
                                <span className="font-bold">{moduleIndex + 1}</span>
                              )}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-slate-900 dark:text-white">
                                {module.name}
                              </h3>
                              <div className="flex items-center gap-3 text-sm text-slate-500">
                                <span>{module.lessons?.length || 0} leçons</span>
                                <span>•</span>
                                <span>{moduleProgress}% complété</span>
                              </div>
                            </div>
                            
                            <Progress value={moduleProgress} className="w-20 h-2 hidden sm:block" />
                            
                            {isExpanded ? (
                              <ChevronDown className="w-5 h-5 text-slate-400" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-slate-400" />
                            )}
                          </button>
                          
                          {/* Lessons */}
                          {isExpanded && (
                            <div className="bg-slate-50 dark:bg-slate-800/50 px-4 py-2">
                              {module.lessons?.filter(l => l.is_published).map((lesson, lessonIndex) => {
                                const LessonIcon = lessonTypeIcons[lesson.lesson_type] || FileText;
                                const completed = isLessonCompleted(lesson.id);
                                
                                return (
                                  <button
                                    key={lesson.id}
                                    onClick={() => navigate(`/student/courses/${courseId}/lessons/${lesson.id}`)}
                                    className="w-full p-3 flex items-center gap-3 hover:bg-white dark:hover:bg-slate-700 rounded-lg transition-colors text-left my-1"
                                    data-testid={`lesson-${lesson.id}`}
                                  >
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                      completed 
                                        ? 'bg-emerald-100 text-emerald-600' 
                                        : 'bg-white dark:bg-slate-700 text-slate-500'
                                    }`}>
                                      {completed ? (
                                        <CheckCircle className="w-4 h-4" />
                                      ) : (
                                        <LessonIcon className="w-4 h-4" />
                                      )}
                                    </div>
                                    
                                    <div className="flex-1 min-w-0">
                                      <p className={`font-medium ${
                                        completed 
                                          ? 'text-slate-500 line-through' 
                                          : 'text-slate-900 dark:text-white'
                                      }`}>
                                        {lesson.name}
                                      </p>
                                      <div className="flex items-center gap-2 text-xs text-slate-500">
                                        <span>{lesson.lesson_type === 'text' ? 'Texte' :
                                               lesson.lesson_type === 'video' ? 'Vidéo' :
                                               lesson.lesson_type === 'pdf' ? 'PDF' :
                                               lesson.lesson_type === 'quiz' ? 'Quiz' :
                                               lesson.lesson_type}</span>
                                        {lesson.duration_minutes && (
                                          <>
                                            <span>•</span>
                                            <span>{lesson.duration_minutes} min</span>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    
                                    <ChevronRight className="w-4 h-4 text-slate-400" />
                                  </button>
                                );
                              })}
                              
                              {module.lessons?.filter(l => l.is_published).length === 0 && (
                                <div className="p-4 text-center text-slate-500 text-sm">
                                  Aucune leçon disponible
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Mobile Progress Card */}
            <Card className="lg:hidden">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-violet-600">
                      {progress?.progress_percent || 0}%
                    </div>
                    <p className="text-xs text-slate-500">Progression</p>
                  </div>
                  <div className="flex-1">
                    <Progress value={progress?.progress_percent || 0} className="h-2 mb-2" />
                    {nextLesson ? (
                      <Button 
                        size="sm"
                        className="w-full bg-violet-600 hover:bg-violet-700"
                        onClick={() => navigate(`/student/courses/${courseId}/lessons/${nextLesson.lesson.id}`)}
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Continuer
                      </Button>
                    ) : (
                      <Button 
                        size="sm"
                        className="w-full bg-violet-600 hover:bg-violet-700"
                        onClick={() => {
                          const firstLesson = course.modules?.[0]?.lessons?.[0];
                          if (firstLesson) {
                            navigate(`/student/courses/${courseId}/lessons/${firstLesson.id}`);
                          }
                        }}
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Commencer
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Course Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">À propos du cours</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {course.trainer_name && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Formateur</p>
                      <p className="font-medium text-slate-900 dark:text-white">
                        {course.trainer_name}
                      </p>
                    </div>
                  </div>
                )}
                
                <Separator />
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Modules</p>
                    <p className="font-semibold text-slate-900 dark:text-white">
                      {course.module_count || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">Leçons</p>
                    <p className="font-semibold text-slate-900 dark:text-white">
                      {course.lesson_count || 0}
                    </p>
                  </div>
                  {(course.duration_calculated?.total_minutes > 0 || course.duration_hours) && (
                    <div>
                      <p className="text-slate-500">Durée totale</p>
                      <p className="font-semibold text-slate-900 dark:text-white">
                        {course.duration_calculated?.total_minutes > 0
                          ? course.duration_calculated.formatted
                          : `${course.duration_hours}h`}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-slate-500">Complétées</p>
                    <p className="font-semibold text-slate-900 dark:text-white">
                      {progress?.completed_lessons || 0}/{progress?.total_lessons || course.lesson_count || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Certificate */}
            {progress?.progress_percent === 100 && (
              <Card className="border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20">
                <CardContent className="p-4 text-center">
                  <Award className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                  <h3 className="font-semibold text-emerald-800 dark:text-emerald-300 mb-1">
                    Félicitations !
                  </h3>
                  <p className="text-sm text-emerald-600 dark:text-emerald-400 mb-3">
                    Vous avez terminé ce cours
                  </p>
                  <Button 
                    variant="outline" 
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                    onClick={() => toast.info('Téléchargement du certificat en cours...')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger le certificat
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default StudentCoursePage;
